import { Button } from '@daily/shared/components/Button';
import { CloseIcon } from '@daily/shared/components/Icons';
import { Text } from '@daily/shared/components/Text';
import { VisuallyHidden } from '@daily/shared/components/VisuallyHidden';
import { useTheme } from '@daily/shared/contexts/Theme';
import { useClosable } from '@daily/shared/hooks/useClosable';
import classNames from 'classnames';
import { ChatMessage, Reaction, useChatState } from 'contexts/ChatProvider';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import tinykeys from 'tinykeys';

interface Props {
  message: ChatMessage;
  onReact(): void;
  showAll?: boolean;
}

const defaultReactions: Reaction[] = ['👏', '👍', '❤️', '😂', '🤯', '🎉'];

export const ChatReactions: React.FC<Props> = ({
  message,
  onReact,
  showAll,
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { reactToMessage } = useChatState();
  const [isAdding, setIsAdding] = useState(false);
  const reactionsRef = useRef<HTMLDivElement>(null);
  const { mediaQueries } = useTheme();

  const handleClick = (reaction: Reaction) => {
    reactToMessage(message, reaction);
    onReact();
    setIsAdding(false);
  };

  const reactions = Object.values(message.reactions ?? {});
  const emptyReactions = defaultReactions.filter(
    (r) =>
      !(r in (message?.reactions ?? {})) ||
      (message?.reactions?.[r]?.count ?? 0) === 0
  );
  const showAdd =
    (reactions.length < defaultReactions.length ||
      reactions.some(({ count }) => !count)) &&
    !isAdding &&
    !showAll;

  const handleAddClick = () => {
    setIsAdding((a) => !a);
  };

  useClosable(
    {
      onClose: () => {
        setIsAdding(false);
      },
      ref: reactionsRef,
    },
    [isAdding]
  );

  /**
   * Setup focus shift when opening & closing reactions.
   */
  useEffect(() => {
    if (isAdding || showAll) {
      // When additional reactions are opened, shift focus to first revealed reaction.
      (
        reactionsRef.current.querySelector(
          'button:not(.hasCount)'
        ) as HTMLButtonElement
      )?.focus();
    }

    if (!isAdding && !showAll) return;

    const unsubscribe = tinykeys(document.body, {
      Escape: (ev) => {
        if (isAdding) {
          // Shift focus back to Add reaction button when closed
          ev.preventDefault();
          ev.stopImmediatePropagation();
          setIsAdding(false);
          (
            reactionsRef.current.querySelector(
              'button.add-reaction'
            ) as HTMLButtonElement
          )?.focus();
        }
      },
    });
    return () => {
      unsubscribe();
    };
  }, [isAdding, showAll]);

  return (
    <div className="chat-reactions" ref={reactionsRef}>
      {defaultReactions
        .filter(
          (r) =>
            !showAdd || isAdding || showAll || message.reactions?.[r]?.count > 0
        )
        .sort((a, b) => {
          if (emptyReactions.includes(a) && !emptyReactions.includes(b))
            return 1;
          if (!emptyReactions.includes(a) && emptyReactions.includes(b))
            return -1;
          return 0;
        })
        .map((reaction) => {
          const hasReacted = message.reactions?.[reaction]?.hasReacted;
          const count = message.reactions?.[reaction]?.count;
          return (
            <Button
              key={reaction}
              className={classNames({
                hasReacted,
                hasCount: count > 0,
              })}
              onClick={() => handleClick(reaction)}
              variant="secondary"
              size="small"
            >
              {reaction}
              {count > 0 ? <Text>{count >= 100 ? '99+' : count}</Text> : null}
            </Button>
          );
        })}
      {showAdd && (
        <Button
          className="add-reaction"
          onClick={handleAddClick}
          variant="secondary"
          size="small"
          title={t('chat.addReaction')}
        >
          <CloseIcon size={16} rotate={45} variant="thick" />
          <VisuallyHidden>{t('chat.addReaction')}</VisuallyHidden>
        </Button>
      )}
      <style jsx>{`
        .chat-reactions {
          display: inline-flex;
          flex-wrap: wrap;
          gap: 2px;
        }
        .chat-reactions :global(button) {
          display: flex;
          gap: 4px;
          height: 24px;
          justify-content: center;
          min-width: 24px;
          padding: 3px 8px 5px;
          position: relative;
          z-index: 1;
        }
        @media ${mediaQueries.coarse} {
          .chat-reactions :global(button) {
            font-size: 24px;
            height: 34px;
          }
        }
        .chat-reactions :global(button.hasReacted) {
          background: ${colors.backgroundAccent};
        }
        .chat-reactions :global(button:hover),
        .chat-reactions :global(button:focus) {
          z-index: 2;
        }
        .chat-reactions :global(.add-reaction) {
          padding: 4px 8px;
        }
      `}</style>
    </div>
  );
};
